import { saveAs } from "file-saver"; // To trigger the download of the ZIP file
import JSZip from "jszip";
import React, { useEffect, useState } from "react";
import LoadingSpinner from "./LoadingSpinner";
import getCurrentDateTime from "./getCurrentDateTime";

function App() {
  const [images, setImages] = useState([]);
  const [watermarkText, setWatermarkText] = useState("대구광명정사 광명화");
  const [opacity, setOpacity] = useState(1);
  const [textColor, setTextColor] = useState("rgba(255, 255, 255, 0.5)");
  const [fontSize, setFontSize] = useState(40);
  const [rotation, setRotation] = useState(-30);
  const [positionX, setPositionX] = useState(-60);
  const [positionY, setPositionY] = useState(15);
  const [fontWeight, setFontWeight] = useState("normal");
  const [previewImages, setPreviewImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Loading state

  // Handle image file uploads
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
  };

  // Draw watermark on an image and return data URL
  const drawWatermark = (imageFile, scaleFactor = 1.0) => {
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const objectURL = URL.createObjectURL(imageFile);
        img.src = objectURL;

        img.onload = () => {
          // Resize canvas to a smaller size for preview
          const canvasWidth = img.width * scaleFactor;
          const canvasHeight = img.height * scaleFactor;
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;

          ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear canvas
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height); // Draw image on canvas

          // Apply watermark text
          ctx.font = `${fontWeight} ${fontSize * scaleFactor}px Arial`; // Scale font size with the image
          ctx.fillStyle = textColor;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.globalAlpha = opacity;

          const text = watermarkText;
          const textWidth = ctx.measureText(text).width;
          const textHeight = fontSize * scaleFactor; // Adjust for text height

          // Calculate watermark position (based on user inputs)
          const xPos = (canvas.width * positionX) / 100;
          const yPos = (canvas.height * positionY) / 100;

          // Apply rotation
          ctx.save();
          ctx.translate(xPos, yPos);
          ctx.rotate((rotation * Math.PI) / 180); // Convert rotation to radians

          // Set the spacing for horizontal and vertical
          // const horizontalSpacing = textWidth * 1.5; // Space between tiles horizontally
          const horizontalSpacing = textWidth * 0.8;
          const verticalSpacing = textHeight * 1.5; // Space between tiles vertically

          // Iterate over the canvas and draw the watermark in a staggered grid pattern
          for (let y = 0; y < canvas.height; y += verticalSpacing) {
            for (let x = 0; x < canvas.width; x += horizontalSpacing) {
              const staggerOffset =
                (y / verticalSpacing) % 2 === 0 ? 0 : horizontalSpacing / 2; // Stagger every other row
              ctx.fillText(
                text,
                (x + staggerOffset + textWidth / 2) * 1.5,
                (y + textHeight / 2) * 1.5
              ); // Draw the text with staggered offset
            }
          }

          ctx.restore();

          // Resolve with the data URL of the image with watermark
          const dataUrl = canvas.toDataURL("image/png");
          resolve(dataUrl);
        };

        img.onerror = (error) => {
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  };

  // Update preview images whenever customization options change

  useEffect(() => {
    setIsLoading(true);

    const updatedPreviewImages = images.map((image) => {
      return drawWatermark(image)
        .then((dataUrl) => {
          return dataUrl;
        })
        .catch((error) => {
          console.error("Error drawing watermark:", error);
          return null;
        });
    });

    Promise.all(updatedPreviewImages).then((previewImagesData) => {
      setPreviewImages(previewImagesData.filter((data) => data !== null)); // Remove null values
      setIsLoading(false); // Set loading to false once processing is done
    });
  }, [
    watermarkText,
    opacity,
    textColor,
    fontSize,
    rotation,
    positionX,
    positionY,
    images,
    fontWeight,
  ]);

  // Create a ZIP file and trigger download
  const handleDownloadBatch = () => {
    const zip = new JSZip();

    previewImages.forEach((imageDataUrl, index) => {
      const filename = `watermarked_image_${index + 1}.png`;
      zip.file(filename, imageDataUrl.split("base64,")[1], { base64: true });
    });

    const zipFilename = `워터마크_${getCurrentDateTime()}.zip`;

    // Generate the ZIP file and trigger download
    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, zipFilename);
    });
  };

  const isDownloadDisabled = isLoading || previewImages.length === 0;

  return (
    <div
      className="App"
      style={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      <div
        style={{
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "10px",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
          marginBottom: "20px",
          flexDirection: "column",
          display: "flex",
          alignItems: "center",
        }}
      >
        <h1>😺워터마크 입히기 - 엄마거💗</h1>

        {/* Image Upload */}
        <input type="file" multiple onChange={handleImageUpload} />

        {/* Customization Inputs */}
        <div>
          <label>워터마크 텍스트: </label>
          <input
            type="text"
            value={watermarkText}
            onChange={(e) => setWatermarkText(e.target.value)}
          />
        </div>

        <div>
          <label>글씨 색깔: </label>
          <select
            value={textColor}
            onChange={(e) => setTextColor(e.target.value)}
          >
            <option value="rgba(255, 255, 255, 0.5)">흰 글씨 연하게</option>
            <option value="rgba(255, 255, 255, 0.8)">흰 글씨 진하게</option>
            <option value="rgba(0, 0, 0, 0.5)">검은 글씨 연하게</option>
            <option value="rgba(0, 0, 0, 0.8)">검은 글씨 진하게</option>
          </select>
        </div>

        <div>
          <label>투명도: </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={opacity}
            onChange={(e) => setOpacity(e.target.value)}
          />
          <span>{opacity}</span>
        </div>

        <div>
          <label>글씨 크기: </label>

          {/* Slider for adjusting font size */}
          <input
            type="range"
            min="10"
            max="200"
            value={fontSize}
            onChange={(e) => setFontSize(e.target.value)}
            style={{ marginRight: "10px" }}
          />

          {/* Textbox for manually entering font size */}
          <input
            type="number"
            value={fontSize}
            min="10"
            max="100"
            onChange={(e) => setFontSize(e.target.value)}
            style={{ width: "60px" }}
          />
        </div>

        <div>
          <label>워터마크 각도: </label>
          <input
            type="range"
            min="-180"
            max="180"
            value={rotation}
            onChange={(e) => setRotation(e.target.value)}
          />
          <input
            type="number"
            value={rotation}
            min="10"
            max="100"
            onChange={(e) => setRotation(e.target.value)}
            style={{ width: "60px" }}
          />
        </div>

        <div>
          <label>좌우 이동: </label>
          <input
            type="range"
            min="-200"
            max="200"
            value={positionX}
            onChange={(e) => setPositionX(e.target.value)}
          />
          <span>{positionX}</span>
        </div>

        <div>
          <label>상하 이동: </label>
          <input
            type="range"
            min="-200"
            max="200"
            value={positionY}
            onChange={(e) => setPositionY(e.target.value)}
          />
          <span>{positionY}</span>
        </div>

        {/* Font Weight Selection */}
        <div>
          <label>글씨 굵기: </label>
          <div>
            <label>
              <input
                type="radio"
                name="fontWeight"
                value="normal"
                checked={fontWeight === "normal"}
                onChange={(e) => setFontWeight(e.target.value)}
              />
              보통
            </label>
            <label>
              <input
                type="radio"
                name="fontWeight"
                value="bold"
                checked={fontWeight === "bold"}
                onChange={(e) => setFontWeight(e.target.value)}
              />
              굵게
            </label>
          </div>
        </div>

        <span
          style={{
            marginTop: "2em",
            marginBottom: "1em",
            textAlign: "center",
          }}
        >
          💗 설정 바꿀 때마다 로딩 있을 수 있엉 💗
          <br />
          💗 설정 다 하고 다운로드 버튼 클릭하면 압축파일 받아졍 💗
          <br />
          💗 쫀하루~~ 😸 💗
          <br />
        </span>

        {/* Download Button */}
        <button
          disabled={isDownloadDisabled}
          onClick={handleDownloadBatch}
          style={{
            fontSize: "1.5em",
            padding: "10px 20px",
            backgroundColor: "rgb(229, 166, 166)",
            color: "white",
            border: "none",
            borderRadius: "20px",
            cursor: isDownloadDisabled ? "not-allowed" : "pointer",
          }}
        >
          다운로드
        </button>
      </div>

      {/* Display Loading Spinner or Preview Images */}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div
          className="image-preview"
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {previewImages.map((previewImage, index) => (
            <img
              key={index}
              src={previewImage}
              alt={`Preview ${index + 1}`}
              style={{ width: "400px", height: "auto", margin: "5px" }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default App;
